import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Menu,
  MenuItem,
  MenuItems,
  MenuButton,
  Transition,
} from '@headlessui/react';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
  InboxIcon,
  BuildingStorefrontIcon,
} from '@heroicons/react/24/outline';
import { getPromoterEvents } from '../../redux/actions/events';

const EventThumbnail = ({ src, alt }) => {
  const [errored, setErrored] = useState(!src);
  return (
    <div className="w-14 h-14 *:rounded-lg border border-gray-200 overflow-hidden shadow-sm flex items-center justify-center shrink-0">
      {errored ? (
        <BuildingStorefrontIcon
          className="w-8 h-8 text-gray-200"
          aria-hidden="true"
        />
      ) : (
        <img
          src={src}
          alt={alt}
          className="object-cover w-full h-full"
          onError={() => setErrored(true)}
          loading="lazy"
        />
      )}
    </div>
  );
};

const SkeletonItem = () => (
  <div className="flex w-full items-center justify-start px-4 py-3 rounded-lg bg-white animate-pulse">
    <div className="w-14 h-14 rounded-lg bg-gray-200 shrink-0" />
    <div className="flex flex-col ml-3 w-48 space-y-2">
      <div className="h-3 bg-gray-200 rounded w-5/6" />
      <div className="h-3 bg-gray-200 rounded w-2/3" />
      <div className="h-4 bg-gray-200 rounded w-1/2" />
    </div>
    <div className="ml-auto h-7 w-16 bg-gray-200 rounded-lg" />
  </div>
);

const EmptyState = ({ message }) => (
  <div className="flex flex-col items-center justify-center text-center px-4 py-6">
    <InboxIcon className="w-10 h-10 text-gray-300" aria-hidden="true" />
    <p className="mt-2 text-sm text-gray-500">{message}</p>
  </div>
);

const ErrorState = ({ onRetry, message }) => (
  <div className="flex items-start gap-3 px-4 py-3 text-red-700 bg-red-50 rounded-lg">
    <ExclamationTriangleIcon className="w-5 h-5 mt-0.5" aria-hidden="true" />
    <div className="text-sm">
      <p className="text-red-600/80">{message || 'Failed to load events.'}</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-2 inline-flex items-center gap-1 rounded-md border border-red-200 px-2.5 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
      >
        Try again
      </button>
    </div>
  </div>
);

const formatYMD = (ymd) => {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1);
  return Number.isNaN(dt.getTime())
    ? ymd
    : dt.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
      });
};

const toLocalEOD = (yyyyMmDd) =>
  yyyyMmDd ? new Date(`${yyyyMmDd}T23:59:59.999`) : null;

const pickStartEnd = (ev) => {
  if (!ev?.date) return { start: null, end: null };

  if (ev.date.type === 'fixed') {
    const start = ev.date.startDate
      ? new Date(`${ev.date.startDate}T00:00:00`)
      : ev.date.endDate
        ? new Date(`${ev.date.endDate}T00:00:00`)
        : null;
    const end = toLocalEOD(ev.date.endDate);
    return { start, end };
  }

  if (
    ev.date.type === 'segmented' &&
    Array.isArray(ev.date.days) &&
    ev.date.days.length
  ) {
    const sorted = [...ev.date.days].sort((a, b) =>
      a.date.localeCompare(b.date)
    );
    const start = new Date(`${sorted[0].date}T00:00:00`);
    const end = toLocalEOD(sorted[sorted.length - 1].date);
    return { start, end };
  }

  return { start: null, end: null };
};

const eventDateLabel = (ev) => {
  if (!ev?.date) return '';
  if (ev.date.type === 'fixed') {
    const a = ev.date.startDate || ev.date.endDate;
    const b = ev.date.endDate || ev.date.startDate;
    if (!a && !b) return '';
    return a && b && a !== b
      ? `${formatYMD(a)} – ${formatYMD(b)}`
      : formatYMD(a || b);
  }
  if (
    ev.date.type === 'segmented' &&
    Array.isArray(ev.date.days) &&
    ev.date.days.length
  ) {
    const sorted = [...ev.date.days].sort((x, y) =>
      x.date.localeCompare(y.date)
    );
    const first = sorted[0].date;
    const last = sorted[sorted.length - 1].date;
    return first !== last
      ? `${formatYMD(first)} – ${formatYMD(last)}`
      : formatYMD(first);
  }
  return '';
};

const InviteMenu = ({
  onInvite,
  triggerLabel,
  triggerClassName,
  triggerIcon,
  showChevron = true,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [hasFetched, setHasFetched] = useState(false);

  const { promoterEvents, loading, error } = useSelector((state) => ({
    promoterEvents: state.events.promoterEvents || [],
    loading: state.events.loading,
    error: state.events.error,
  }));

  const upcomingEvents = useMemo(() => {
    const now = new Date();
    return (
      (promoterEvents ?? [])
        // A brand can't open a draft, and the API refuses the invite.
        .filter((e) => String(e?.status || '').toLowerCase() !== 'draft')
        .map((e) => ({ e, ...pickStartEnd(e) }))
        .filter(({ start }) => start && start > now)
        .sort((a, b) => (a.start?.getTime() ?? 0) - (b.start?.getTime() ?? 0))
        .map(({ e }) => e)
    );
  }, [promoterEvents]);

  const retry = () => dispatch(getPromoterEvents());

  return (
    <Menu as="div" className="relative inline-block text-left">
      {({ open }) => (
        <>
          <MenuButton
            onClick={() => {
              if (!hasFetched) {
                dispatch(getPromoterEvents());
                setHasFetched(true);
              }
            }}
            className={
              triggerClassName ??
              'inline-flex items-center justify-between gap-3 rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-yellow-05 text-sm font-medium text-white hover:bg-orange-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400'
            }
          >
            <span className="inline-flex items-center gap-2">
              {triggerIcon}
              {triggerLabel ?? t('event_detail.invite')}
            </span>
            {showChevron ? (
              open ? (
                <ChevronUpIcon className="w-5 h-5" aria-hidden="true" />
              ) : (
                <ChevronDownIcon className="w-5 h-5" aria-hidden="true" />
              )
            ) : null}
          </MenuButton>

          <Transition
            show={open}
            enter="transition ease-out duration-150"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <MenuItems
              // ✅ Mobile/tablet: left aligned + viewport width
              // ✅ Desktop: right aligned + fixed-ish width
              className="
                absolute left-0 mt-2
                w-[calc(100vw-2rem)] max-w-[420px]
                origin-top-left
                rounded-xl p-2 z-50 shadow-xl bg-white ring-1 ring-gray-200 focus:outline-none

                md:left-auto md:right-0 md:origin-top-right
              "
            >
              <div className="py-1 max-h-72 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                {loading && (
                  <div className="space-y-2 px-1">
                    <SkeletonItem />
                    <SkeletonItem />
                    <SkeletonItem />
                  </div>
                )}

                {!loading && error && (
                  <ErrorState onRetry={retry} message={error} />
                )}

                {!loading && !error && upcomingEvents.length === 0 && (
                  <EmptyState message={t('listing.no_events')} />
                )}

                {!loading && !error && upcomingEvents.length > 0 && (
                  <ul className="space-y-2">
                    {upcomingEvents.map((event) => (
                      <MenuItem key={event.id}>
                        {({ active }) => (
                          <button
                            type="button"
                            onClick={() => onInvite(event)}
                            className={`${
                              active
                                ? 'ring-1 ring-gray-200 bg-gray-50'
                                : 'bg-white'
                            } flex w-full rounded-lg items-center px-4 py-3 text-sm transition-all duration-150`}
                          >
                            <EventThumbnail
                              src={event.avatar}
                              alt={event.name}
                            />

                            <div className="flex flex-col items-start ml-3 min-w-0">
                              <span className="font-semibold text-gray-900 truncate max-w-[180px] sm:max-w-[240px]">
                                {event.name}
                              </span>

                              {event.location && (
                                <span className="mt-0.5 inline-flex max-w-[140px] truncate items-center gap-1 text-xs text-gray-600">
                                  {event.location}
                                </span>
                              )}

                              {eventDateLabel(event) && (
                                <span className="mt-1 inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md font-medium bg-gray-100 text-gray-700 w-fit">
                                  {eventDateLabel(event)}
                                </span>
                              )}
                            </div>

                            <span className="ml-auto inline-flex items-center gap-1 bg-green-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors duration-150 hover:bg-green-700 shrink-0">
                              {t('event_detail.invite')}
                            </span>
                          </button>
                        )}
                      </MenuItem>
                    ))}
                  </ul>
                )}
              </div>
            </MenuItems>
          </Transition>
        </>
      )}
    </Menu>
  );
};

export default InviteMenu;
